import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Search, Plus, Upload, RefreshCw, ExternalLink, 
  ArrowRight, Filter, BarChart3, Target, TrendingUp,
  Building2, Globe, Mail, Phone, MoreVertical, Trash2, Eye, MapPin
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminLayout";
import { API } from "../../App";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const INDUSTRIES = {
  hospitality: "Hospitality",
  trades: "Trades",
  health_wellness: "Health & Wellness",
  professional_services: "Professional Services",
  retail: "Retail",
  other: "Other"
};

const PRIORITY_COLORS = {
  high: "bg-red-500/20 text-red-400 border-red-500/30",
  medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  low: "bg-gray-500/20 text-gray-400 border-gray-500/30"
};

const STATUS_COLORS = {
  new: "bg-blue-500/20 text-blue-400",
  scanning: "bg-purple-500/20 text-purple-400",
  scanned: "bg-green-500/20 text-green-400",
  qualified: "bg-emerald-500/20 text-emerald-400",
  pushed: "bg-gray-500/20 text-gray-400",
  archived: "bg-gray-700/20 text-gray-500"
};

const LeadEngine = () => {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    industry: "",
    search: ""
  });

  useEffect(() => {
    fetchProspects();
    fetchStats();
  }, [filters]);

  const fetchProspects = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      if (filters.industry) params.append("industry", filters.industry);
      if (filters.search) params.append("search", filters.search);
      
      const response = await axios.get(`${API}/lead-engine/prospects?${params}`);
      setProspects(response.data.prospects);
    } catch (error) {
      toast.error("Failed to load prospects");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/lead-engine/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats", error);
    }
  };

  const handleScan = async (prospectId) => {
    try {
      await axios.post(`${API}/lead-engine/prospects/${prospectId}/scan`);
      toast.success("Scan started");
      // Refresh after a delay
      setTimeout(fetchProspects, 2000);
    } catch (error) {
      toast.error("Failed to start scan");
    }
  };

  const handleDelete = async (prospectId) => {
    if (!window.confirm("Delete this prospect?")) return;
    try {
      await axios.delete(`${API}/lead-engine/prospects/${prospectId}`);
      toast.success("Prospect deleted");
      fetchProspects();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete prospect");
    }
  };

  const handlePushToPipeline = async (prospectId) => {
    try {
      const response = await axios.post(`${API}/lead-engine/prospects/${prospectId}/push-to-pipeline`);
      toast.success("Pushed to Pipeline");
      fetchProspects();
    } catch (error) {
      toast.error("Failed to push to Pipeline");
    }
  };

  return (
    <AdminLayout title="Lead Engine">
      <div className="space-y-6" data-testid="lead-engine">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Lead Engine</h2>
            <p className="text-sm text-[#8A8A8A]">Identify and prioritise prospects</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/lead-engine/finder" className="btn-secondary text-sm inline-flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Find Prospects
            </Link>
            <Link to="/admin/lead-engine/import" className="btn-secondary text-sm inline-flex items-center gap-2">
              <Upload className="w-4 h-4" /> Import
            </Link>
            <Link to="/admin/lead-engine/add" className="btn-primary text-sm inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Prospect
            </Link>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-xs text-[#8A8A8A]">Total Prospects</div>
            </div>
            <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-4">
              <div className="text-2xl font-bold text-red-400">{stats.by_priority.high}</div>
              <div className="text-xs text-[#8A8A8A]">High Priority</div>
            </div>
            <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-4">
              <div className="text-2xl font-bold text-yellow-400">{stats.by_priority.medium}</div>
              <div className="text-xs text-[#8A8A8A]">Medium Priority</div>
            </div>
            <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-4">
              <div className="text-2xl font-bold text-green-400">{stats.by_status.scanned}</div>
              <div className="text-xs text-[#8A8A8A]">Scanned</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
            <input
              type="text"
              placeholder="Search prospects..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="input-field pl-10 w-full"
            />
          </div>
          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            className="input-field"
          >
            <option value="">All Priorities</option>
            <option value="high">High Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="low">Low Priority</option>
          </select>
          <select
            value={filters.industry}
            onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
            className="input-field"
          >
            <option value="">All Industries</option>
            {Object.entries(INDUSTRIES).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="input-field"
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="scanned">Scanned</option>
            <option value="pushed">Pushed</option>
          </select>
        </div>

        {/* Prospects Table */}
        <div className="bg-[#1C1C1C] border border-[#2E2E2E] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#8A8A8A]">Loading...</div>
          ) : prospects.length === 0 ? (
            <div className="p-8 text-center">
              <Target className="w-12 h-12 mx-auto mb-4 text-[#5F8FB8] opacity-50" />
              <p className="text-[#8A8A8A] mb-4">No prospects yet</p>
              <Link to="/admin/lead-engine/add" className="btn-primary text-sm inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add First Prospect
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2E2E2E] text-left text-xs text-[#8A8A8A] uppercase">
                    <th className="p-4">Company</th>
                    <th className="p-4">Industry</th>
                    <th className="p-4">Priority</th>
                    <th className="p-4">Scores</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Reason</th>
                    <th className="p-4 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {prospects.map((prospect) => (
                    <tr key={prospect.prospect_id} className="border-b border-[#2E2E2E] hover:bg-[#252525]">
                      <td className="p-4">
                        <div className="font-medium">{prospect.company_name}</div>
                        <a 
                          href={prospect.website_url.startsWith('http') ? prospect.website_url : `https://${prospect.website_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#5F8FB8] hover:underline flex items-center gap-1"
                        >
                          {prospect.website_url} <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-[#8A8A8A]">
                          {INDUSTRIES[prospect.industry] || prospect.industry}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs uppercase border ${PRIORITY_COLORS[prospect.priority]}`}>
                          {prospect.priority}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-3 text-xs">
                          <div title="Intent">
                            <span className="text-[#8A8A8A]">I:</span>
                            <span className="ml-1 font-mono">{prospect.intent_score}</span>
                          </div>
                          <div title="Need">
                            <span className="text-[#8A8A8A]">N:</span>
                            <span className="ml-1 font-mono">{prospect.need_score}</span>
                          </div>
                          <div title="Ability">
                            <span className="text-[#8A8A8A]">A:</span>
                            <span className="ml-1 font-mono">{prospect.ability_score}</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs ${STATUS_COLORS[prospect.status]}`}>
                          {prospect.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-xs text-[#8A8A8A] max-w-xs truncate" title={prospect.reason_to_contact}>
                          {prospect.reason_to_contact || "—"}
                        </p>
                      </td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-2 hover:bg-[#2E2E2E] rounded">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-[#1C1C1C] border-[#2E2E2E]">
                            <DropdownMenuItem asChild>
                              <Link to={`/admin/lead-engine/${prospect.prospect_id}`} className="flex items-center gap-2">
                                <Eye className="w-4 h-4" /> View Details
                              </Link>
                            </DropdownMenuItem>
                            {prospect.status === "new" && (
                              <DropdownMenuItem onClick={() => handleScan(prospect.prospect_id)} className="flex items-center gap-2">
                                <RefreshCw className="w-4 h-4" /> Scan Website
                              </DropdownMenuItem>
                            )}
                            {prospect.status === "scanned" && prospect.priority !== "low" && (
                              <DropdownMenuItem onClick={() => handlePushToPipeline(prospect.prospect_id)} className="flex items-center gap-2">
                                <ArrowRight className="w-4 h-4" /> Push to Pipeline
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDelete(prospect.prospect_id)} className="flex items-center gap-2 text-red-400">
                              <Trash2 className="w-4 h-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default LeadEngine;
